import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import getExplorerLink, { ExplorerSection } from 'helpers/explorer-links';

import SorobanTokenIcon from 'assets/icons/small-icons/icon-soroban-token-16.svg';

import { cardBoxShadow } from 'styles/mixins';
import { COLORS, Z_INDEX } from 'styles/style-constants';

const HINT_OFFSET = 8;
// Below this much room overhead the hint would run off the viewport, so it flips.
const HINT_FLIP_THRESHOLD = 48;

const BadgeLink = styled.a`
    display: inline-flex;
    vertical-align: text-bottom;
    margin-left: 0.4rem;
    color: inherit;

    /* Sized in em so the badge keeps its proportion to whatever text it joins. */
    svg {
        width: 0.9em;
        height: 0.9em;
    }
`;

const Hint = styled.div<{ $top: number; $left: number; $below: boolean }>`
    position: fixed;
    top: ${({ $top }) => $top}px;
    left: ${({ $left }) => $left}px;
    transform: translate(-50%, ${({ $below }) => ($below ? '0' : '-100%')});
    /* Matches basics/Tooltip so the hint reads as the same component. */
    padding: 0.9rem 1.2rem;
    border-radius: 0.5rem;
    background-color: ${COLORS.purple400};
    color: ${COLORS.white};
    font-size: 1.4rem;
    line-height: 2rem;
    white-space: nowrap;
    pointer-events: none;
    z-index: ${Z_INDEX.modal + 1};
    ${cardBoxShadow};

    // triangle
    &::after {
        content: '';
        display: block;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        border-left: 0.6rem solid ${COLORS.transparent};
        border-right: 0.6rem solid ${COLORS.transparent};

        ${({ $below }) =>
            $below
                ? `
                    bottom: 100%;
                    border-bottom: 0.6rem solid ${COLORS.purple400};
                `
                : `
                    top: 100%;
                    border-top: 0.6rem solid ${COLORS.purple400};
                `}
    }
`;

type Props = {
    contract: string;
    className?: string;
};

// Marks a token as living on Soroban once its home domain takes over the domain
// slot, and keeps the contract one click away.
//
// The hint is portalled to the body instead of reusing the shared Tooltip: the
// badge sits inside ellipsized asset labels, scrollable lists and modal bodies,
// and an absolutely positioned tooltip is clipped by every one of them.
const SorobanTokenBadge = ({ contract, className }: Props): React.ReactNode => {
    const linkRef = useRef<HTMLAnchorElement>(null);
    const [hint, setHint] = useState<{ top: number; left: number; below: boolean } | null>(null);

    const showHint = useCallback(() => {
        const rect = linkRef.current?.getBoundingClientRect();

        if (!rect) {
            return;
        }

        const below = rect.top < HINT_FLIP_THRESHOLD;

        setHint({
            top: below ? rect.bottom + HINT_OFFSET : rect.top - HINT_OFFSET,
            left: rect.left + rect.width / 2,
            below,
        });
    }, []);

    const hideHint = useCallback(() => setHint(null), []);

    useEffect(() => {
        if (!hint) {
            return;
        }

        // Viewport coordinates go stale as soon as anything moves under them.
        window.addEventListener('scroll', hideHint, true);
        window.addEventListener('resize', hideHint);

        return () => {
            window.removeEventListener('scroll', hideHint, true);
            window.removeEventListener('resize', hideHint);
        };
    }, [hint, hideHint]);

    return (
        <>
            <BadgeLink
                ref={linkRef}
                className={className}
                href={getExplorerLink(ExplorerSection.asset, contract)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Soroban token"
                onMouseEnter={showHint}
                onMouseLeave={hideHint}
                onFocus={showHint}
                onBlur={hideHint}
                onClick={event => event.stopPropagation()}
            >
                <SorobanTokenIcon />
            </BadgeLink>
            {hint &&
                createPortal(
                    <Hint $top={hint.top} $left={hint.left} $below={hint.below}>
                        Soroban token
                    </Hint>,
                    document.body,
                )}
        </>
    );
};

export default SorobanTokenBadge;
